































































































import { Component, Watch, Vue, Mixins } from 'vue-property-decorator';
import { Button, Card, Checkbox, Option, Select, Tooltip } from 'element-ui';
import NavBar from '@/components/layout/NavBar.vue';
import NavBarNavigation from '@/components/layout/NavBarNavigation.vue';

// mixins
import customerOrganizations from '@/mixins/customerOrganizationOptions';
import fromTo from '@/mixins/fromTo';
import pagination from '@/mixins/pagination';

// gql
import fetchBillableShipmentListQuery
  from '@/graphql/queries/shipment/FetchBillableShipmentList.gql';
import { shipperService } from '@/services/ShipperService';
import { billService } from '@/services/BillService';

// types
import {
  ShipmentScopedInput,
  CustomerFields,
  ShipmentFields,
  AvailableLocalesEnum
} from '@/types/graphql/schemaTypes';
import { CheckboxStatus, NavigationSection, SelectOption } from '@/types/types';

import { languageOptions } from '@/constants/languages';

@Component({
  apollo: {
    shipments: {
      query: fetchBillableShipmentListQuery,
      variables() {
        return {
          billArgs: { unbilled: true, quoteAcceptedAtOrder: this.quoteOrder },
          shipmentArgs: { customerOrganizationId: this.selectedCustomerOrganizationId || null },
          pagination: {
            page: parseInt(this.$route.query.page, 10) || this.currentPage,
            per: parseInt(this.$route.query.size, 10) || this.paginationSize
          }
        };
      },
      update(data) {
        this.totalCount = data ? data.shipmentsPagination.totalCount : 0;
        this.updateParent(data.shipments?.length);
        return data && data.shipments;
      },
      fetchPolicy: 'no-cache'
    }
  },
  components: {
    NavBar,
    NavBarNavigation,
    'el-button': Button,
    'el-card': Card,
    'el-checkbox': Checkbox,
    'el-option': Option,
    'el-select': Select,
    'el-tooltip': Tooltip
  }
})
export default class SelectShipment extends Mixins(customerOrganizations, fromTo, pagination) {
  public selectedCustomerOrganizationId: string | null = null;
  public selectedShipmentIds: string[] = [];
  public quoteOrder = 'asc';
  public isCreating = false;
  private readonly languageOptions = languageOptions();
  private language: AvailableLocalesEnum = AvailableLocalesEnum.ja;
  private shipments: ShipmentFields[];

  private itemSelect = false;
  private allShipments: CheckboxStatus = {
    checked: false,
    indeterminate: false
  }

  // Disable select-all checkbox when no filter or no items
  private isCheckAllDisabled() {
    return !this.selectedCustomerOrganizationId || this.shipments?.length === 0;
  };

  // Reset select-all checkbox when search bar changed/cleared
  private clearSelection() {
    this.selectedShipmentIds = [];
    this.allShipments = {
      checked: false,
      indeterminate: false
    };
  }

  // Select/Diselect all shipments
  private handleCheckAllChange(value: boolean): void {
    // Top Bottom
    this.shipments.forEach(shipment => {
      this.changeShipment(shipment, value);
    });
    this.allShipments.indeterminate = false;
  }

  /**
   * @param shipment :checked shipment
   * @param value    :whether shipment is checked
   * @param bubble   :whether update should bubble
   */
  // Change shipments according to the customer selected in the filter field
  private changeShipment(shipment: ShipmentFields, value, bubble = false): void {
    if (value) {
      if (!this.selectedCustomerOrganizationId) {
        this.filterShipments(shipment);
      }
      this.addShipment(shipment);
    } else {
      this.removeShipment(shipment);
    }
    // Bottom Top
    if (bubble) {
      this.updateParent(this.shipments.length);
    }
  }

  /**
   * @param totalCount :number of selected shipments
   */
  // Update the select-all checkbox depending on its children
  private updateParent(totalCount: number) {
    const checkedCount = this.selectedShipmentIds.length;
    if (checkedCount === 0) { // none checked
      this.allShipments.checked = false;
      this.allShipments.indeterminate = false;
    } else if (checkedCount < totalCount) { // indeterminate
      this.allShipments.checked = false;
      this.allShipments.indeterminate = true;
    } else { // all checked
      this.allShipments.checked = true;
      this.allShipments.indeterminate = false;
    }
  }

  /**
   * @param shipment :checked shipment
   */
  // Check where shipment is selected
  private isShipmentSelected(shipment: ShipmentFields): boolean {
    return this.selectedShipmentIds.includes(shipment.id);
  }

  private selectItems(): void {
    if (this.selectedShipmentIds.length > 0) {
      this.$emit('selectShipment', this.selectedShipmentIds);
    }
  }

  private toggleSort(): void {
    this.quoteOrder = this.quoteOrder === 'desc' ? 'asc' : 'desc';
  }

  private addShipment(shipment: ShipmentFields): void {
    if (!this.isShipmentSelected(shipment)) {
      this.selectedShipmentIds.push(shipment.id);
    }
  }

  private filterShipments(shipment) {
    this.searchCustomerOrganization = shipment.customerOrganization.name;
    this.selectedCustomerOrganizationId = shipment.customerOrganization.id;
  }

  private removeShipment(shipment: ShipmentFields): void {
    this.selectedShipmentIds.splice(this.selectedShipmentIds.indexOf(shipment.id), 1);
  }
}
